import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "loading", "pressed", "icon", "onPress", "onFocus", "onBlur", "variant", "size", "autoFocus"];
import styled from 'styled-components';
import { useRef } from 'react';
import { useButton, mergeProps } from 'react-aria';
import { getSizeTokens, getVariantTokens } from './Button.helpers.ts';
import { useVariantCssObject, useSizeCssObject } from './Button.hooks';
import { useTrellisFocusRing } from '../../hooks/useTrellisFocusRing';
import { Text } from '../Text';
import { Flex } from '../Flex';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const StyledButton = styled.button.withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-1xfrdsk-0"
})(({
  $variantCssStyles,
  $sizeCssStyles,
  $focusRingStyles
}) => {
  return Object.assign({
    all: 'unset',
    cursor: 'pointer'
  }, $variantCssStyles, $sizeCssStyles, $focusRingStyles);
});
export function Button(_ref) {
  let {
      children,
      disabled = false,
      loading = false,
      pressed,
      icon,
      onPress = () => {},
      onFocus = () => {},
      onBlur = () => {},
      variant = 'primary',
      size = 'medium',
      autoFocus = false
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handlePress = () => {
    if (disabled || loading) return;
    onPress();
  };
  const handleFocus = () => {
    onFocus();
  };
  const handleBlur = () => {
    onBlur();
  };
  const variantTokens = getVariantTokens(variant);
  const variantCssStyles = useVariantCssObject(variantTokens);
  const {
    focusRingStyles,
    focusProps
  } = useTrellisFocusRing({
    autoFocus
  });
  const sizeTokens = getSizeTokens(size, variant);
  const sizeCssStyles = useSizeCssObject(sizeTokens);
  const buttonRef = useRef(null);
  const {
    buttonProps
  } = useButton(Object.assign({}, props, {
    'aria-pressed': pressed,
    autoFocus,
    onPress: handlePress,
    onFocus: handleFocus,
    onBlur: handleBlur
  }), buttonRef);
  const mergedProps = mergeProps(buttonProps, focusProps);

  // Temporary in order to set the text color for buttons while
  // we figure out if the `Text` component should/shouldn't set
  // a default color.
  // We can use the `idle` state directly because the component uses
  // the same text color for all states (for now).
  const textColor = variantTokens.idle.color;
  return /*#__PURE__*/_jsx(StyledButton, Object.assign({}, mergedProps, {
    ref: buttonRef,
    "aria-disabled": disabled || loading ? true : undefined,
    $variantCssStyles: variantCssStyles,
    $sizeCssStyles: sizeCssStyles,
    $focusRingStyles: focusRingStyles,
    children: /*#__PURE__*/_jsx(Text, {
      as: "p",
      typography: sizeTokens.typography,
      color: textColor,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        columnGap: sizeTokens.gap,
        as: "span",
        children: [icon && icon, children]
      })
    })
  }));
}